import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export interface PageProgress {
  completed: number;
  total: number;
}

interface SetPageProgressOptions {
  /** Keep a value the page already published, so a later background read cannot replace it. */
  ifAbsent?: boolean;
}

interface ProspectPageProgressValue {
  byHref: Record<string, PageProgress>;
  setPageProgress: (href: string, progress: PageProgress, options?: SetPageProgressOptions) => void;
}

const ProspectPageProgressContext = createContext<ProspectPageProgressValue>({
  byHref: {},
  setPageProgress: () => {},
});

export function ProspectPageProgressProvider({ children }: { children: ReactNode }) {
  const [byHref, setByHref] = useState<Record<string, PageProgress>>({});

  const setPageProgress = useCallback((href: string, progress: PageProgress, options?: SetPageProgressOptions) => {
    setByHref((current) => {
      const existing = current[href];
      if (options?.ifAbsent && existing) return current;
      if (existing?.completed === progress.completed && existing.total === progress.total) {
        return current;
      }
      return { ...current, [href]: progress };
    });
  }, []);

  return (
    <ProspectPageProgressContext.Provider value={{ byHref, setPageProgress }}>
      {children}
    </ProspectPageProgressContext.Provider>
  );
}

export function usePageProgress(href: string): PageProgress | null {
  return useContext(ProspectPageProgressContext).byHref[href] ?? null;
}

export function useSetPageProgress(): (
  href: string,
  progress: PageProgress,
  options?: SetPageProgressOptions,
) => void {
  return useContext(ProspectPageProgressContext).setPageProgress;
}

export function usePageProgressByHref(): Record<string, PageProgress> {
  return useContext(ProspectPageProgressContext).byHref;
}
